import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Box, Stack, Typography } from '@mui/material';
import { Link2Off } from 'lucide-react';
import { Button as UiButton } from '@repo/ui';

import { AuthErrorBoundary, AuthErrorMessage } from 'components/auth';
import { PoweredByVera } from 'components/UI';
import { CompanyContext } from 'contexts/companyContext';
import { ROUTES } from 'routes';
import { useSelector } from 'store/hooks';
import { useClaimTransferByToken } from 'hooks/api/transfers/useClaimTransferByToken';
import { useGetCompanyStyles } from 'hooks/api';

import { OnboardingShell } from '../components/OnboardingShell';
import { OnboardingAuthPanel } from '../components/OnboardingAuthPanel';

/**
 * Case-insensitive address comparison, trimmed at both ends.
 *
 * The link's `email` is the address the transfer was ADDRESSED to (the API sets it
 * from the recipient at send time — see `buildClaimUrl`), so comparing it against
 * the signed-in profile's own address answers exactly "am I the person this link
 * was written for". Blank on either side is never a match, so a profile that has
 * not loaded yet cannot be mistaken for agreement.
 */
const isSameAddress = (a?: string | null, b?: string | null): boolean =>
  !!a && !!b && a.trim().toLowerCase() === b.trim().toLowerCase();

/**
 * VRG-500 — the wording this page opens with, for the three states it can be in.
 *
 * `isNew` is deliberately THREE-valued, not a boolean:
 *
 *   - `true`  — a first-time recipient. Gets an INVITATION naming the brand,
 *               because they have never heard of Vera and "verify your email to
 *               claim your item" tells them nothing about what they are joining.
 *   - `false` — a returning owner. Gets a SIGN-IN, because they already have an
 *               account and being invited to join something they are already in
 *               reads as a mistake.
 *   - `null`  — unknown. Gets today's neutral copy unchanged.
 *
 * The third state is load-bearing rather than defensive tidiness. The hint that
 * populates it is stamped into the link at send time, so every claim link ALREADY
 * IN SOMEONE'S INBOX carries no hint at all, and will not until those links
 * expire. Collapsing `null` into either branch would word all of that existing
 * mail with a guess; leaving it neutral means the change is strictly additive and
 * an old link behaves exactly as it does today.
 *
 * `brandName` is never defaulted to "Vera" or to any other name. `apps/app` is
 * multi-tenant, so a hardcoded brand is somebody else's brand on somebody else's
 * invitation, and a wrong name in an invitation is worse than no name — it is
 * actively misleading about who is writing to you. Every string below therefore
 * has to stand on its own without one.
 */
const claimCopy = ({
  isNew,
  brandName,
  hasToken,
}: {
  isNew: boolean | null;
  brandName?: string;
  hasToken: boolean;
}): { heading: string; subheading: string } => {
  // Six sentences rather than a lead plus a shared tail. A shared tail is what the
  // first cut used, and it produced "Verify your email to claim your item. You'll
  // finish claiming your item by scanning it." — the same phrase twice in twelve
  // words, because the no-token tail already contains the object the lead names.
  // Writing them out keeps each one readable at the cost of some repetition here.
  //
  // Sentences, not em-dashes (working-conventions §15).
  if (isNew === true) {
    return {
      heading: brandName
        ? `You've been invited to join the ${brandName} program`
        : "You've been invited to claim your item",
      subheading: hasToken
        ? 'Verify your email to create your account and claim your item. No scan needed.'
        : "Verify your email to create your account. You'll finish claiming your item by scanning it.",
    };
  }

  if (isNew === false) {
    return {
      heading: 'Sign in to claim your item',
      subheading: hasToken
        ? 'Verify your email to claim your item. No scan needed.'
        : 'Verify your email, then finish claiming your item by scanning it.',
    };
  }

  return {
    heading: 'Claim your item',
    subheading: hasToken
      ? 'Verify your email to claim your item. No scan needed.'
      : 'Verify your email, then finish claiming your item by scanning it.',
  };
};

/**
 * VERA-304 — the terminal state for a link this page cannot act on: no `email` or
 * no `certificateId`, so there is nothing to verify and nothing to claim.
 *
 * ## Why it does not use `OnboardingShell`
 *
 * The shell is a STEP in a flow: top-aligned at 20vh, left-ranged, sized for a form.
 * This is not a step, it is the end of one, and rendered through the shell it read as
 * a bare heading and a paragraph hard against the top-left corner, which looks
 * unstyled rather than designed. Terminal states in this app centre in the viewport
 * (`NotFound`), so this one does too.
 *
 * ## The circle is a member of an existing family, not a new invention
 *
 * 56px, `grey[50]` fill, `grey[300]` hairline, a 24px lucide glyph at the repo's
 * `strokeWidth={1}` line weight: the same treatment `EmptyCertificates` and
 * `CertificatesLoadError` share and document as siblings. THEME TOKENS rather than
 * the literal `#f2f2f2`/`#e6e6e6` that `CertificatesLoadError` still carries —
 * `apps/app` is multi-tenant and a hardcoded grey is a Vera grey rendered inside
 * someone else's brand. `EmptyCertificates` records that correction; this follows it.
 *
 * `NotFound`'s 72px accent-tinted circle is the third treatment in the app and was
 * NOT copied: the ask here was explicitly a grey circle, and grey is also what keeps
 * this proportionate (below).
 *
 * ## The glyph is deliberately dull
 *
 * `AuthenticChip` rejected a `ShieldAlert` because it "said 'this might be fake'
 * while the drawer it opens says a shared link is normal — the icon was grading the
 * item, and lying". The same discipline applies in the opposite direction here: the
 * link genuinely IS broken, so a fault signal is honest, but the fault is "this URL
 * did not work", not "something is wrong with your item" and not a security warning.
 * `Link2Off` says exactly that and nothing more. An alarm triangle, or a red tint,
 * would put a scare on a page whose most likely cause is an out-of-date email.
 */
const UnusableClaimLink = () => (
  <Box
    sx={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      px: 3,
      py: 4,
      textAlign: 'center',
    }}
  >
    {/* `flex: 1` on the message and the footer after it is what pins the attribution
        to the bottom without absolute positioning, so it never overlaps the copy on a
        short viewport. */}
    <Stack sx={{ flex: 1, justifyContent: 'center', alignItems: 'center', maxWidth: 400 }}>
      <Box
        // The heading below says the same thing in words, so announcing the glyph
        // too would just repeat it to a screen reader.
        aria-hidden
        sx={theme => ({
          width: 56,
          height: 56,
          borderRadius: '50%',
          backgroundColor: theme.palette.grey[50],
          border: `1px solid ${theme.palette.grey[300]}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 2,
        })}
      >
        <Link2Off size={24} strokeWidth={1} color='currentColor' />
      </Box>
      <Typography sx={{ fontWeight: 500, color: 'text.primary', mb: 0.5, fontSize: '16px' }}>
        This claim link didn't work
      </Typography>
      <Typography sx={{ color: 'text.secondary', fontSize: '14px', lineHeight: 1.5 }}>
        It may be from an older email, or it may be incomplete. Open the most recent claim
        email you received, or ask the sender for a new link.
      </Typography>
    </Stack>
    {/*
      No `hidden` prop, and it is not an oversight: the white-label entitlement
      (VERA-644) is read from `certificate.hide_powered_by`, and this state exists
      precisely BECAUSE there is no certificate id to fetch one with. Nothing on this
      page is branded either — no logo, no theme, no product — so it reads as a Vera
      surface rather than as attribution stamped on someone else's page. If the
      entitlement ever needs honouring here, it has to arrive some other way than the
      certificate.
    */}
    <PoweredByVera />
  </Box>
);

/**
 * VERA-839 / VRG-138 — Claim-Ownership Sign-Up.
 *
 * Landing route for the "claim ownership" invite email (`claim-ownership-invite`
 * template, sent by `SendClaimOwnershipInviteUseCase`) and for the claim-token
 * link embedded in the transfer/gift-init emails (`ClaimCertificateByTokenUseCase`).
 * Carries `certificateId`, `email`, optionally `companyId`, and — when a claim
 * token was issued at transfer init — `token` as query params.
 *
 * VRG-138: when `token` is present, this page completes the ownership
 * transfer itself right after magic-auth verification succeeds — no physical
 * re-scan required (`POST /transfer/customer/claim`). Without a token (e.g.
 * the standalone `SendClaimOwnershipInviteUseCase` invite, which isn't tied
 * to an issued token), this page falls back to the legacy hand-off: account
 * creation only, then the user finishes on the certificate page's existing
 * "Accept transfer" flow (re-scan the physical tag).
 *
 * VERA-304 — the auth panel is no longer unconditional. Once every transfer and
 * gift email routes here rather than at the certificate page, a recipient who is
 * ALREADY signed in as themselves was being asked to verify, by one-time code, the
 * very address their live session is for. The old certificate-page destination had
 * no such step, so routing here introduced the friction; this removes it. The panel
 * still stands for everyone else, including a DIFFERENT signed-in user following
 * someone else's link, who must never auto-claim.
 */
const ClaimOwnershipSignUp = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setCompanyId } = useContext(CompanyContext);
  const { isAuthenticated, profile } = useSelector(({ auth }) => auth);
  const { mutateAsync: claimTransferByToken } = useClaimTransferByToken();
  const [claimError, setClaimError] = useState<string | null>(null);
  const [isClaiming, setIsClaiming] = useState(false);

  const email = searchParams.get('email') ?? '';
  const certificateId = searchParams.get('certificateId') ?? undefined;
  const companyId = searchParams.get('companyId') ?? undefined;
  const token = searchParams.get('token') ?? undefined;

  /**
   * VRG-500 — new-vs-returning, in two halves, because the page has to word itself
   * BEFORE it is allowed to know the answer.
   *
   * Half one, `recipientHint`: stamped into the link by the API at send time
   * (`buildClaimUrl`). The page cannot simply ask, because the only identifier it
   * holds is an `email` sitting in an editable address bar — an endpoint answering
   * "does this address have an account" for an arbitrary email is an
   * account-enumeration oracle over real people. So the API, which already knows
   * who it addressed the mail to, says so in the mail.
   *
   * That makes the hint STALE if the person signed up between the email and the
   * click, and SPOOFABLE because it is a query param. Both are acceptable here and
   * only here: it selects a sentence. It must never gate an action, and it does
   * not — the claim itself is authorised by `token` and by the verified session,
   * neither of which this touches.
   *
   * Half two, `verifiedHasAccount`: the authoritative answer, from the verify
   * response. It lands after the code is entered and OVERRIDES the hint from that
   * point on, so a stale or tampered hint corrects itself on screen rather than
   * persisting through the rest of the flow.
   */
  const recipientHint = searchParams.get('recipient');
  const [verifiedHasAccount, setVerifiedHasAccount] = useState<boolean | null>(null);

  // Shares react-query's `['companyStyles', companyId]` cache with the App-level
  // theme fetch, so this is the same request the shell already makes rather than a
  // second round trip. `name` is VRG-500's addition to that payload.
  const { data: companyStyles } = useGetCompanyStyles({ companyId });
  const brandName = companyStyles?.name;

  /**
   * Verified answer first, hint second, neither third. Only the two values the API
   * actually stamps are recognised: anything else in the param — a typo, a probe,
   * a truncated link — falls through to `null` and the neutral copy, rather than
   * being coerced into one of the two branches.
   */
  const isNewRecipient: boolean | null =
    verifiedHasAccount !== null
      ? !verifiedHasAccount
      : recipientHint === 'new'
        ? true
        : recipientHint === 'returning'
          ? false
          : null;

  const { heading, subheading } = claimCopy({
    isNew: isNewRecipient,
    brandName,
    hasToken: !!token,
  });

  /**
   * Three states, not two, because the profile arrives on its own request.
   *
   * `checkAuthSaga` flips `isAuthenticated` from stored tokens and only THEN
   * dispatches `GET_PROFILE`, so there is a window where a session exists and the
   * address behind it is still unknown. Treating that window as "not the recipient"
   * would flash the OTP panel at the very person this change exists to spare, and
   * treating it as a match would auto-claim on an identity nobody has established.
   *
   * The wait is BOUNDED, because `getProfileSaga` swallows its own errors.
   * A failed profile request leaves `profile` null with `isAuthenticated` still true
   * and nothing to retry it, so an unbounded wait would park the page on a
   * placeholder permanently — a worse dead end than the OTP step this change removes.
   * On timeout the page falls through to the panel, which is exactly the right
   * fallback: it re-establishes identity by code rather than guessing at one.
   */
  const [hasWaitedForProfile, setHasWaitedForProfile] = useState(false);

  useEffect(() => {
    if (!isAuthenticated || profile) return;
    const timer = setTimeout(() => setHasWaitedForProfile(true), 5000);
    return () => clearTimeout(timer);
  }, [isAuthenticated, profile]);

  const isIdentityResolving = isAuthenticated && !profile && !hasWaitedForProfile;
  const isSignedInAsRecipient =
    isAuthenticated && isSameAddress(profile?.email, email);

  useEffect(() => {
    if (companyId) {
      setCompanyId(companyId);
    }
  }, [companyId, setCompanyId]);

  const goToCertificate = useCallback(() => {
    navigate(certificateId ? `/certificates/${certificateId}` : ROUTES.CERTIFICATES);
  }, [navigate, certificateId]);

  const completeClaim = useCallback(async () => {
    if (!token) {
      // No claim token on this link (e.g. the standalone claim-ownership
      // invite) — fall back to the legacy hand-off: the user finishes on the
      // certificate page's existing physical re-scan flow.
      goToCertificate();
      return;
    }

    setIsClaiming(true);
    setClaimError(null);
    try {
      await claimTransferByToken({ token });
      goToCertificate();
    } catch (err: any) {
      setClaimError(
        err?.response?.data?.message ||
          'This claim link is invalid or has expired. Please contact the sender for a new one.',
      );
    } finally {
      setIsClaiming(false);
    }
  }, [token, goToCertificate, claimTransferByToken]);

  /**
   * The signed-in recipient's claim runs itself.
   *
   * `hasAutoClaimed` is a ref rather than state on purpose: it must be set in the
   * same tick the request is fired, so a re-render mid-flight (the profile object
   * being replaced, a query settling) cannot start a second claim. The token is
   * single-use, and the second attempt would fail with "invalid or has expired" on
   * a claim that had in fact just succeeded.
   */
  const hasAutoClaimed = useRef(false);

  useEffect(() => {
    if (!isSignedInAsRecipient || hasAutoClaimed.current) return;
    if (!email || !certificateId) return;

    hasAutoClaimed.current = true;
    completeClaim();
  }, [isSignedInAsRecipient, email, certificateId, completeClaim]);

  if (!email || !certificateId) {
    return <UnusableClaimLink />;
  }

  if (isIdentityResolving) {
    return (
      // Deliberately NOT the resolved heading. This branch means a session exists
      // whose owner is still unknown, and it resolves to the signed-in-recipient
      // screen below far more often than not — so borrowing the invitation heading
      // here would swap it out a beat later, which reads worse than the neutral one
      // it replaced.
      <OnboardingShell heading='Claim your item'>
        {/* Placeholder at the height the panel's own first step occupies, so the
            resolved surface does not jump into a different position. */}
        <div className='h-[92px] w-full animate-pulse rounded-md bg-gray-100' />
      </OnboardingShell>
    );
  }

  if (isSignedInAsRecipient) {
    return (
      <AuthErrorBoundary>
        <OnboardingShell
          heading='Claim your item'
          subheading={
            claimError
              ? undefined
              : `You're signed in as ${profile?.email}. No code needed.`
          }
        >
          <AuthErrorMessage message={claimError} />
          {isClaiming && <p className='text-sm text-gray-500'>Claiming your item…</p>}
          {/* A failed auto-claim must not be a dead end. The certificate page carries
              its own claim card for a signed-in recipient, so it is a real way
              forward rather than a consolation link. */}
          {claimError && (
            <UiButton variant='primary' fullWidth onClick={goToCertificate}>
              View your item
            </UiButton>
          )}
        </OnboardingShell>
      </AuthErrorBoundary>
    );
  }

  return (
    <AuthErrorBoundary>
      <OnboardingShell heading={heading} subheading={subheading}>
        <AuthErrorMessage message={claimError} />
        <OnboardingAuthPanel
          email={email}
          companyId={companyId}
          // VRG-500: the result is READ, not discarded. `hasAccount` is the
          // authoritative answer to the question the link only hinted at, so
          // recording it re-words the heading for the rest of the flow whenever the
          // hint was stale (they signed up after the email was sent) or tampered
          // with. `isNewUser` is deliberately ignored here: it answers "first time
          // at this company", which is FALSE for the brand-new gift recipient this
          // page exists to greet.
          onVerified={({ hasAccount }) => {
            setVerifiedHasAccount(hasAccount);
            completeClaim();
          }}
        />
        {isClaiming && (
          <p className='mt-4 text-sm text-gray-500'>Claiming your item…</p>
        )}
      </OnboardingShell>
    </AuthErrorBoundary>
  );
};

export default ClaimOwnershipSignUp;
